import React, { useState } from 'react';
import SidebarContent from './SidebarContent';
import { BarChart3, Bot, ChevronDown, ChevronRight, FileBarChart, FileText, LayoutDashboard, Receipt, Settings, ShoppingCart, Wallet, X } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import useUserAccount from '../../hooks/stateHooks/userAccountHook';
import { SIDE_BAR_NAVS } from '../../resources/constants';
import { DASHBOARD } from '../../resources/links';
import { GoOrganization } from 'react-icons/go';
import { ISideBarItem } from '../../resources/interfaces';
import { sidebarCategory } from '../../resources/types';
import { capitalize } from 'lodash';
import clsx from 'clsx';
import useRoute from '../../hooks/routesHook';
import Avatar from '../elements/Avatar';

interface IProps {
  showSidebar?: boolean;
  onMenuItemClicked?: () => void;
  onCloseSidebar?: () => void;
  mobileOpen?: boolean;
  collapsed?: boolean;
  onSetMobileOpen?: () => void;
}

function Sidebar(props: IProps) {
  const { user, nameInitials } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const [expandedCategories, setExpandedCategories] = useState({
    dashboard: true,
    accounts: true,
    financing: true,
    manage: true,
    system: true
  })

  const getSidebarNavs = () => {
    return SIDE_BAR_NAVS;
  };

  const isActiveNav = (navPath: string): boolean => {
    const path = window.location.pathname;
    // Split the path and get the relevant segment based on the navPath
    const pathSegments = path.split('/').filter(Boolean);
    const navPathSegment = navPath.replace(/^\//, ''); // Remove leading slash if present

    // For base dashboard, check if we're at the root
    if (navPath === '/') {
      return path === "/admin" || path === `/admin/`;
    }

    // For other paths, check for exact match of the last segment
    return pathSegments[pathSegments.length - 1] === navPathSegment;
  };

  const toggleCategory = (category: sidebarCategory) => {
    setExpandedCategories((prev: any) => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const userHasAccess = (sideBarItem: ISideBarItem): boolean => {
    if (user.role && user.company && (sideBarItem.access.includes(user.role) && sideBarItem.access.includes(user.company?.type))) {
      return true;
    }
    return false;
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-slate-200 bg-white transition-all dark:border-slate-800 dark:bg-slate-900 ${props.collapsed ? "w-[76px]" : "w-64"
        } ${props.mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
    >
      <div className="flex h-16 items-center justify-between border-b border-slate-200 px-4 dark:border-slate-800">
        {/* NavBrand */}
        <div>
          <span className="border-none flex items-center">
            <div>
              {user.company?.logo ? (
                <Link to={DASHBOARD} className='w-[50px] h-[50px]'>
                  <img className="w-full h-full" />
                </Link>

              ) : (
                <div className='flex items-center gap-3 overflow-hidden'>
                  <div className='bg-gray-200 rounded-[50%] mr-2 w-[30px] h-[30px] md:w-[50px] md:h-[50px] flex items-center justify-center border'>
                    <GoOrganization className='w-full h-full p-1 md:p-2 text-white dark:text-slate-500' />
                  </div>
                  {!props.collapsed &&
                    <h3 className='font-bold text-md md:text-xl text-ellipsis whitespace-nowrap overflow-hidden ...'>
                      <span className="whitespace-nowrap text-md md:text-lg font-bold">{user.company?.name}</span>
                    </h3>
                  }
                </div>
              )}
            </div>
          </span>
        </div>

        <button
          type="button"
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden dark:hover:bg-slate-800"
          onClick={() => props.onSetMobileOpen?.()}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {Object.keys(SIDE_BAR_NAVS).map((category: string) => (
          getSidebarNavs()[category as sidebarCategory].filter((nav: ISideBarItem) => userHasAccess(nav)).length > 0 && (
            <div className={clsx("",
              { ["pt-2 px-2"]: !props.collapsed }
            )}>
              {!props.collapsed && (
                <button
                  onClick={() => toggleCategory(category as sidebarCategory)}
                  className="w-full flex items-center justify-between py-2 text-sm font-semibold dark:text-white hover:dark:text-gray-200 text-gray-900 rounded-md"
                >
                  <span>{capitalize(category)}</span>
                  {expandedCategories[category as sidebarCategory] ?
                    <ChevronDown className="h-4 w-4" /> :
                    <ChevronRight className="h-4 w-4" />
                  }
                </button>
              )}

              {/* Navigation Items */}
              <div className={clsx(
                `mt-1 space-y-1 border-gray-100 ${expandedCategories[category as sidebarCategory] ? '' : 'hidden'}`,
                { ["border-l-2 pl-2"]: !props.collapsed }
              )}>
                {getSidebarNavs()[category as sidebarCategory].filter((nav: ISideBarItem) => userHasAccess(nav))
                  .map((nav: ISideBarItem, index: number) => {
                    return (
                      <div key={index} className="flex items-center justify-center w-full">
                        {/* <Tooltip message={nav.title} direction="right"> */}
                        <NavLink
                          onClick={props.onCloseSidebar}
                          key={index}
                          to={`${getDashboardBase()}${nav.path}`}
                          className={clsx(
                            'cursor-pointer w-full px-3 py-2 text-sm flex items-center rounded-md',
                            { [' bg-primary-main text-white']: isActiveNav(nav.path) },
                            { ['hover:bg-primary-surface hover:dark:text-gray-700']: !isActiveNav(nav.path) }
                          )}>
                          {nav.icon && (
                            <span className="mr-3" title={nav.title}>
                              <nav.icon className=" h-5 w-5" />
                            </span>
                          )}
                          {!props.collapsed && <h2 className="w-full">{nav.title}</h2>}
                        </NavLink>
                        {/* </Tooltip> */}
                      </div>
                    )
                  }
                  )}
              </div>
            </div>
          )
        ))}
      </nav>

      <div className="border-t border-slate-200 p-3 dark:border-slate-800">
        <div className={`flex items-center gap-3 rounded-xl bg-slate-50 p-2.5 dark:bg-slate-800 ${props.collapsed ? "justify-center" : ""}`}>
          {/* <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-bold dark:bg-slate-700">
            {nameInitials}
          </div> */}
          <Avatar
            nameInitials={nameInitials}
            src={user.profileImage}
            size='xs'
          />
          {!props.collapsed && (
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">
                {user ? `${user.firstName} ${user.lastName}` : "User"}
              </p>
              <p className="truncate text-xs text-slate-500">
                {capitalize(user.role)}
              </p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
